/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BaseButton from "components/common/Button/Base";
import {
  PAYMENT_NOTICE_MATCH_STATUS_LABELS,
  PaymentNoticeUploadResult,
  SimpleBusiness,
} from "models/paymentNotice.model";
import { getSimpleBusinessList } from "pages/remotes";
import { PALETTE } from "styles/base/variables";

interface ResultDialogProps {
  isOpen: boolean;
  title: string;
  results: PaymentNoticeUploadResult[];
  onClose: () => void;
}

function ResultDialog({ isOpen, title, results, onClose }: ResultDialogProps) {
  const [businessNameMap, setBusinessNameMap] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!isOpen) return;

    getSimpleBusinessList()
      .then((res) => {
        const businessList: SimpleBusiness[] =
          res.data?.businessDtos || res.data || [];
        setBusinessNameMap(
          businessList.reduce<Record<string, string>>((acc, biz) => {
            acc[biz.id] = biz.name;
            return acc;
          }, {})
        );
      })
      .catch((err) => console.error(err));
  }, [isOpen]);

  const cancelledCount = results.filter((r) => r.cancelled).length;
  const uploadFailCount = results.filter((r) => !r.notice && !r.cancelled).length;
  const matchedCount = results.filter((r) => r.notice?.businessId).length;
  const unmatchedCount =
    results.length - cancelledCount - uploadFailCount - matchedCount;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title} ({results.length}건)
      </DialogTitle>
      <DialogContent>
        <div
          css={css`
            min-width: 400px;
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <div
            css={css`
              font-size: 13px;
              color: ${PALETTE.textMid};
            `}
          >
            매칭 완료 {matchedCount}건
            {unmatchedCount > 0 && ` · 미매칭 ${unmatchedCount}건`}
            {uploadFailCount > 0 && ` · 업로드 실패 ${uploadFailCount}건`}
            {cancelledCount > 0 && ` · 미전송 ${cancelledCount}건`}
          </div>

          <div
            css={css`
              max-height: 400px;
              overflow-y: auto;
              border: 1px solid ${PALETTE.border};
              border-radius: 8px;
            `}
          >
            {results.map((result, idx) => (
              <div
                key={`${result.fileName}-${idx}`}
                css={css`
                  padding: 12px 14px;
                  display: flex;
                  align-items: flex-start;
                  gap: 10px;
                  border-bottom: 1px solid ${PALETTE.borderLight};

                  &:last-child {
                    border-bottom: none;
                  }
                `}
              >
                {result.notice ? (
                  <CheckCircleIcon
                    fontSize="small"
                    css={css`
                      margin-top: 2px;
                      color: ${PALETTE.progressCompleted};
                    `}
                  />
                ) : result.cancelled ? (
                  <RemoveCircleOutlineIcon
                    fontSize="small"
                    css={css`
                      margin-top: 2px;
                      color: ${PALETTE.textMid};
                    `}
                  />
                ) : (
                  <ErrorIcon
                    fontSize="small"
                    css={css`
                      margin-top: 2px;
                      color: ${PALETTE.error};
                    `}
                  />
                )}
                <div
                  css={css`
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                  `}
                >
                  <div
                    css={css`
                      font-size: 13px;
                      color: ${PALETTE.text};
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    `}
                  >
                    {result.fileName}
                  </div>
                  {result.notice ? (
                    <div
                      css={css`
                        font-size: 12px;
                        color: ${PALETTE.textMid};
                      `}
                    >
                      {PAYMENT_NOTICE_MATCH_STATUS_LABELS[
                        result.notice.matchStatus
                      ]}
                      {" · "}
                      {result.notice.businessId
                        ? businessNameMap[result.notice.businessId] ||
                          result.notice.companyName ||
                          "-"
                        : "미매칭"}
                    </div>
                  ) : (
                    <div
                      css={css`
                        font-size: 12px;
                        color: ${result.cancelled
                          ? PALETTE.textMid
                          : PALETTE.error};
                      `}
                    >
                      {result.errorMessage || "업로드 중 오류가 발생했습니다."}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
      <div
        css={css`
          margin: 6px 24px;
          border-top: 1px solid ${PALETTE.border};
        `}
      />
      <DialogActions
        css={css`
          padding: 12px 24px 20px 24px;
        `}
      >
        <BaseButton variant="contained" color={PALETTE.primary} onClick={onClose}>
          닫기
        </BaseButton>
      </DialogActions>
    </Dialog>
  );
}

export default ResultDialog;
